// Encode/decode between the `FhirPathPatchOperation` IR and the FHIR
// `Parameters` wire shape for fhirpath-patch
// (http://hl7.org/fhir/fhirpatch.html). Part of the fhirpath-patch delete
// boundary — see ./DELETE-WHEN-MEDPLUM-SHIPS-TRANSPORT-FHIRPATH.md.

import {
  getElementDefinitionFromElements,
  isDataTypeLoaded,
  type TypedValue,
  tryGetDataType,
} from '@medplum/core'
import type {
  Binary,
  Bundle,
  Parameters,
  ParametersParameter,
  Resource,
} from '@medplum/fhirtypes'
import type { Operation } from 'rfc6902'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import type {
  FhirPathPatchOperation,
  FhirPathPatchOpType,
} from '#src/fhirpath-patch/types.ts'
import { SEEN_BASE_REF_PARAM } from '#src/fhirpath-patch/types.ts'
import {
  createReferenceStringWithHistory,
  parseHistoryReferenceString,
} from '#src/references.ts'

// Thrown when a JSON-patch value cannot be represented as a Parameters
// value[x] (e.g. an anonymous BackboneElement). Callers fall back to plain
// JSON Patch for the affected entry.
export class FhirPathPatchEncodeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FhirPathPatchEncodeError'
  }
}

// FHIR primitive datatype names (lowercase on the wire, capitalized in the
// value[x] property name).
const PRIMITIVE_TYPES = new Set([
  'base64Binary',
  'boolean',
  'canonical',
  'code',
  'date',
  'dateTime',
  'decimal',
  'id',
  'instant',
  'integer',
  'markdown',
  'oid',
  'positiveInt',
  'string',
  'time',
  'unsignedInt',
  'uri',
  'url',
  'uuid',
])

const OP_TYPES: ReadonlySet<string> = new Set([
  'add',
  'insert',
  'delete',
  'replace',
  'move',
])

// Resolve the FHIR datatype name at a dotted property path (rooted at the
// resource type, e.g. `Patient.communication.language`) by walking Medplum's
// loaded schema — no shape heuristics. Choice elements (`value[x]`) resolve
// from the concrete property name in the path. Returns undefined when the
// schema cannot name the element (unknown path, primitive-extension `_field`
// siblings, contained-resource hops). Throws `FhirPathPatchEncodeError` when
// the root resource type's schema is not loaded — callers must load it first
// (browser/Node: `medplum.requestSchema(resourceType)`; tests:
// `indexFhirDefinitions()` from medplum-definitions.test-utils).
export function resolveSchemaType(schemaPath: string): string | undefined {
  const [rootType, ...segments] = schemaPath.split('.')
  if (!rootType || segments.length === 0) {
    return undefined
  }
  if (!isDataTypeLoaded(rootType)) {
    throw new FhirPathPatchEncodeError(
      `Schema for ${rootType} is not loaded — call medplum.requestSchema('${rootType}') before building fhirpath-patch entries`
    )
  }

  let currentType = rootType
  for (const segment of segments) {
    const schema = tryGetDataType(currentType)
    if (!schema) {
      return undefined
    }
    const element = getElementDefinitionFromElements(schema.elements, segment)
    if (!element) {
      return undefined
    }
    let code: string | undefined
    if (element.type?.length === 1) {
      code = element.type[0]?.code
    } else {
      // Choice-of-type ('value[x]') — the concrete property name carries the
      // datatype: strip the element's prefix off the path segment.
      const elementKey = element.path?.split('.').at(-1)
      if (!elementKey?.endsWith('[x]')) {
        return undefined
      }
      const suffix = segment.slice(elementKey.length - '[x]'.length)
      if (!suffix) {
        return undefined
      }
      const primitive = `${suffix.charAt(0).toLowerCase()}${suffix.slice(1)}`
      code = PRIMITIVE_TYPES.has(primitive) ? primitive : suffix
    }
    if (!code) {
      return undefined
    }
    currentType = code
  }
  return currentType
}

function valueXKey(type: string): string {
  return `value${type.charAt(0).toUpperCase()}${type.slice(1)}`
}

// Complex datatypes we both infer (inferComplexTypeName) and that are legal
// `Parameters.parameter.value[x]` choices. Notably NOT legal despite being
// inferable: `Extension` (not part of the FHIR R4 open value[x] type set —
// the server rejects `valueExtension` as an invalid property) and resource
// types (a resource travels as `part.resource`, not value[x]).
const ENCODABLE_COMPLEX_TYPES = new Set([
  'Address',
  'Annotation',
  'Attachment',
  'CodeableConcept',
  'Coding',
  'ContactPoint',
  'HumanName',
  'Identifier',
  'Period',
  'Quantity',
  'Reference',
])

function encodeValuePart(value: TypedValue): ParametersParameter {
  if (
    !PRIMITIVE_TYPES.has(value.type) &&
    !ENCODABLE_COMPLEX_TYPES.has(value.type)
  ) {
    throw new FhirPathPatchEncodeError(
      `'${value.type}' is not a legal Parameters value[x] type`
    )
  }
  return { name: 'value', [valueXKey(value.type)]: value.value }
}

function decodeValuePart(part: ParametersParameter): TypedValue | undefined {
  if (part.resource) {
    return { type: part.resource.resourceType, value: part.resource }
  }
  for (const [key, value] of Object.entries(part)) {
    if (key.startsWith('value') && value !== undefined) {
      const suffix = key.slice('value'.length)
      const primitive = `${suffix.charAt(0).toLowerCase()}${suffix.slice(1)}`
      return {
        type: PRIMITIVE_TYPES.has(primitive) ? primitive : suffix,
        value,
      }
    }
  }
  return undefined
}

// Build the `Parameters` wire form for a list of fhirpath-patch operations,
// optionally carrying the Seen transport sidecars (base-resource snapshot +
// versionId the patch was computed against).
export function fhirPathPatchOperationsToParameters(
  operations: readonly FhirPathPatchOperation[],
  sidecars?: { baseResource?: Resource }
): Parameters {
  const parameter: ParametersParameter[] = operations.map((op) => {
    const part: ParametersParameter[] = [
      { name: 'type', valueCode: op.type },
      { name: 'path', valueString: op.path },
    ]
    if (op.name !== undefined) {
      part.push({ name: 'name', valueString: op.name })
    }
    if (op.value !== undefined) {
      part.push(encodeValuePart(op.value))
    }
    if (op.index !== undefined) {
      part.push({ name: 'index', valueInteger: op.index })
    }
    if (op.source !== undefined) {
      part.push({ name: 'source', valueInteger: op.source })
    }
    if (op.destination !== undefined) {
      part.push({ name: 'destination', valueInteger: op.destination })
    }
    return { name: 'operation', part }
  })

  if (sidecars?.baseResource) {
    parameter.push({
      name: SEEN_BASE_REF_PARAM,
      valueString: createReferenceStringWithHistory(sidecars.baseResource),
    })
  }

  return { resourceType: 'Parameters', parameter }
}

// Parse fhirpath-patch operations back out of the `Parameters` wire form,
// ignoring sidecar parameters.
export function parametersToFhirPathPatchOperations(
  parameters: Parameters
): FhirPathPatchOperation[] {
  const operations: FhirPathPatchOperation[] = []
  for (const param of parameters.parameter ?? []) {
    if (param.name !== 'operation') {
      continue
    }
    const fields = new Map<string, ParametersParameter>()
    for (const part of param.part ?? []) {
      if (part.name) {
        fields.set(part.name, part)
      }
    }
    const type = fields.get('type')?.valueCode
    const path = fields.get('path')?.valueString
    if (!type || !OP_TYPES.has(type) || !path) {
      continue
    }
    const valuePart = fields.get('value')
    operations.push({
      type: type as FhirPathPatchOpType,
      path,
      name: fields.get('name')?.valueString,
      value: valuePart ? decodeValuePart(valuePart) : undefined,
      index: fields.get('index')?.valueInteger,
      source: fields.get('source')?.valueInteger,
      destination: fields.get('destination')?.valueInteger,
    })
  }
  return operations
}

interface FhirPathPatchSidecars {
  baseRef: string | undefined
  versionId: string | undefined
}

// Extract the Seen transport sidecar from a fhirpath-patch `Parameters`: the
// base history reference (`Type/id/_history/{versionId}`) and the versionId
// derived from it. The base resource itself is loaded lazily from `baseRef`.
export function getFhirPathPatchSidecars(
  parameters: Parameters
): FhirPathPatchSidecars {
  let baseRef: string | undefined
  for (const param of parameters.parameter ?? []) {
    if (param.name === SEEN_BASE_REF_PARAM) {
      baseRef = param.valueString
    }
  }
  const versionId = baseRef
    ? parseHistoryReferenceString(baseRef).versionId
    : undefined
  return { baseRef, versionId }
}

// True when a bundle-entry resource is a fhirpath-patch `Parameters` payload
// (vs. a Binary JSON-patch payload).
export function isFhirPathPatchParameters(
  resource: Resource | undefined
): resource is Parameters {
  return (
    resource?.resourceType === 'Parameters' &&
    (resource.parameter ?? []).some((p) => p.name === 'operation')
  )
}

// Replace the Seen transport sidecars on a fhirpath-patch `Parameters` in
// place. Used to re-base a stored patch onto a newer resource version: the
// fhirpath ops are element-keyed so they re-resolve safely, and the refreshed
// versionId keeps the submit-time `test /meta/versionId` precondition aligned
// with the version the patch is now lowered against.
export function setFhirPathPatchSidecars(
  parameters: Parameters,
  sidecars: { baseResource: Resource }
): Parameters {
  const parameter = (parameters.parameter ?? []).filter(
    (p) => p.name !== SEEN_BASE_REF_PARAM
  )
  parameter.push({
    name: SEEN_BASE_REF_PARAM,
    valueString: createReferenceStringWithHistory(sidecars.baseResource),
  })
  parameters.parameter = parameter
  return parameters
}

// Re-base every PATCH entry in a bundle onto the given current resource state
// (keyed by `ResourceType/id`). A queued bundle's preconditions are from
// proposal time; after any intervening write their version test would reject
// the whole apply — re-basing is the conflict strategy: re-point each entry's
// `meta.versionId` precondition at the fresh version so its element-keyed ops
// re-resolve against current state. Covers both wire formats:
//   - fhirpath-patch `Parameters`: rewrite the base history-ref sidecar (which
//     carries the version) to the fresh version.
//   - Binary json-patch (the fallback format when a value can't be encoded as
//     fhirpath-patch, e.g. an empty-object `statusReason`): rewrite the
//     leading `test /meta/versionId` op.
// Entries with no matching current resource keep their stored ref (and fail
// loudly at apply if it is stale). Mutates and returns the bundle.
export function rebaseFhirPathPatchBundle(
  bundle: Bundle,
  current: Record<string, Resource>
): Bundle {
  for (const entry of bundle.entry ?? []) {
    if (entry.request?.method !== 'PATCH') {
      continue
    }
    const ref = entry.request.url?.split('?')[0]
    const resource = ref ? current[ref] : undefined
    if (!resource) {
      continue
    }
    if (isFhirPathPatchParameters(entry.resource)) {
      setFhirPathPatchSidecars(entry.resource, { baseResource: resource })
    } else {
      rebaseBinaryJsonPatchEntry(entry.resource, resource.meta?.versionId)
    }
  }
  return bundle
}

// Re-point a Binary json-patch entry's `test /meta/versionId` precondition at
// the fresh versionId (no-op for entries that have no such test or aren't a
// json-patch Binary). Mutates the Binary in place.
function rebaseBinaryJsonPatchEntry(
  resource: Resource | undefined,
  versionId: string | undefined
): void {
  if (
    !versionId ||
    resource?.resourceType !== 'Binary' ||
    resource.contentType !== 'application/json-patch+json' ||
    !resource.data
  ) {
    return
  }
  const binary = resource as Binary
  let ops: Operation[]
  try {
    ops = JSON.parse(base64Decode(resource.data)) as Operation[]
  } catch {
    return
  }
  let changed = false
  for (const op of ops) {
    if (op.op === 'test' && op.path === '/meta/versionId') {
      op.value = versionId
      changed = true
    }
  }
  if (changed) {
    binary.data = base64Encode(JSON.stringify(ops))
  }
}
